import { MenuItem } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { MouseEvent, useEffect, useRef, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import DropdownButton from "react-bootstrap/DropdownButton";
import { MAX_ROWS_IN_DOM } from "../constants";
import { PrinterInfo } from "../services/actionsApi";
import { ActionItem, ColumnConfig, Row, SortState } from "../types";
import { cellValue, formatCell, isSortable, rowKey } from "../utils";
import NextStepButton from "./NextStepButton";
import RequisitionStatusBadge from "./RequisitionStatusBadge";
import ResultFileLink from "./ResultFileLink";
import NoRecord from "Shared/Common/NoRecord";
import PermissionComponent, {
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";
import { ArrowDown, ArrowUp } from "Shared/Icons";
import ArrowBottomIcon from "Shared/SVG/ArrowBottomIcon";
import useLang from "Shared/hooks/useLanguage";
import {
  StyledDropButton,
  StyledDropMenu,
} from "Utils/Style/Dropdownstyle";

interface Props {
  columns: ColumnConfig[];
  rows: Row[];
  loading: boolean;
  hasMore: boolean;
  capped: boolean;
  error: boolean;
  sort: SortState | null;
  selected: Set<number>;
  openId: number | null;
  focusIdx: number;
  busyId: number | null;
  canRemoveDateWindow: boolean;
  scrollerRef: React.RefObject<HTMLDivElement>;
  bulkBar?: React.ReactNode;
  onSort: (column: ColumnConfig) => void;
  onToggleRow: (id: number) => void;
  onToggleAll: () => void;
  onRowClick: (row: Row, index: number) => void;
  onNextStep: (row: Row, file?: File) => void;
  onOpenFile: (row: Row, url: string) => void;
  onLoadMore: () => void;
  onRemoveDateWindow: () => void;
  // v1 row actions
  rowMenuItems: (row: Row) => ActionItem[];
  printers: PrinterInfo[];
  onPrintLabel: (row: Row, printer: PrinterInfo) => void;
  onOpenNotes: (row: Row) => void;
  onOpenFlag: (row: Row, flagId: number) => void;
}

// Row ⋮ menu matching v1 (DropdownButton, controlled so only one is open at a time).
const RowActionDropdown = ({
  items,
  show,
  onToggle,
}: {
  items: ActionItem[];
  show: boolean;
  onToggle: (next: boolean) => void;
}) => {
  const { t } = useLang();
  return (
    <div className="d-flex justify-content-center rotatebtnn">
      <DropdownButton
        show={show}
        onToggle={onToggle}
        className="p-0 del-before btn btn-light-info btn-active-info btn-sm btn-action table-action-btn"
        id="ViewRequisitionV23Dots"
        drop="end"
        title={<i className="bi bi-three-dots-vertical p-0"></i>}
      >
        {items.map((item) =>
          item.permission ? (
            <PermissionComponent
              key={item.key}
              moduleName="Requisition"
              pageName="View Requisition v2"
              permissionIdentifier={item.permission}
            >
              {({ pageName, moduleName, permissionIdentifier }) => (
                <Dropdown.Item
                  eventKey={item.key}
                  onClick={() => item.onClick()}
                >
                  <button
                    role="link"
                    className="px-0 border-0 bg-transparent"
                  >
                    {item.icon && (
                      <i className={`${item.icon} mr-2 w-20px`}></i>
                    )}
                    {getPermissionDisplayName(
                      pageName,
                      moduleName,
                      permissionIdentifier,
                      t(item.label)
                    )}
                  </button>
                </Dropdown.Item>
              )}
            </PermissionComponent>
          ) : (
            <Dropdown.Item
              key={item.key}
              eventKey={item.key}
              onClick={() => item.onClick()}
            >
              <button role="link" className="px-0 border-0 bg-transparent">
                {item.icon && (
                  <i className={`${item.icon} mr-2 w-20px`}></i>
                )}
                {t(item.label)}
              </button>
            </Dropdown.Item>
          )
        )}
      </DropdownButton>
    </div>
  );
};

// Per-row Print Label button matching v1 (StyledDropButton + StyledDropMenu).
const PrintLabelCell = ({
  printers,
  onPrint,
}: {
  printers: PrinterInfo[];
  onPrint: (p: PrinterInfo) => void;
}) => {
  const { t } = useLang();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const handleClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchor(e.currentTarget);
  };
  const handleClose = () => setAnchor(null);
  return (
    <div style={{ width: "max-content" }}>
      <StyledDropButton
        aria-haspopup="true"
        aria-expanded={Boolean(anchor)}
        onClick={handleClick}
        className="btn btn-dark-brown btn-sm py-0 fw-400 fa-1x min-h-25px h-25px w-100px"
      >
        {t("Print Label")}
        <span className="svg-icon svg-icon-5 m-0">
          <ArrowBottomIcon />
        </span>
      </StyledDropButton>
      <StyledDropMenu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        className="w-auto"
      >
        {printers.map((p) => (
          <MenuItem
            key={p.value}
            className="w-auto"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
              onPrint(p);
            }}
          >
            <i className="fa fa fa-print text-warning mr-2 w-20px"></i>
            {p.label}
          </MenuItem>
        ))}
        {printers.length === 0 && (
          <MenuItem disabled>{t("No printers configured")}</MenuItem>
        )}
      </StyledDropMenu>
    </div>
  );
};

// Portal-themed worklist table (v1 table classes) with infinite scroll inside the card.
const WorklistGrid = ({
  columns,
  rows,
  loading,
  hasMore,
  capped,
  error,
  sort,
  selected,
  openId,
  focusIdx,
  busyId,
  canRemoveDateWindow,
  scrollerRef,
  bulkBar,
  onSort,
  onToggleRow,
  onToggleAll,
  onRowClick,
  onNextStep,
  onOpenFile,
  onLoadMore,
  onRemoveDateWindow,
  rowMenuItems,
  printers,
  onPrintLabel,
  onOpenNotes,
  onOpenFlag,
}: Props) => {
  const { t } = useLang();
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef(onLoadMore);
  loadMoreRef.current = onLoadMore;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (e) => e[0].isIntersecting && loadMoreRef.current(),
      { root: scrollerRef.current, rootMargin: "150px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [scrollerRef]);

  // A selective filter can return short pages — keep loading until the viewport is full.
  useEffect(() => {
    const sc = scrollerRef.current;
    if (
      sc &&
      hasMore &&
      !loading &&
      rows.length < MAX_ROWS_IN_DOM &&
      sc.scrollHeight <= sc.clientHeight + 150
    )
      loadMoreRef.current();
  }, [rows.length, loading, hasMore, scrollerRef]);

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(rowKey(r)));
  const colSpan = columns.length + 2; // checkbox + actions

  const renderArrows = (c: ColumnConfig) => {
    if (!isSortable(c)) return null;
    const active = sort?.key === c.key;
    const cls = (d: "asc" | "desc") =>
      `${active && sort?.dir === d ? "text-danger fs-6" : "text-gray-700 fs-7"} p-0 m-0`;
    return (
      <span className="d-flex align-items-center ms-3">
        <ArrowUp CustomeClass={cls("asc")} />
        <ArrowDown CustomeClass={cls("desc")} />
      </span>
    );
  };

  // Special columns keep the v1 behaviour; everything else is plain text.
  const renderCell = (row: Row, c: ColumnConfig) => {
    const value = cellValue(row, c);
    switch (c.key.toLowerCase()) {
      case "requisitionstatus":
        return <RequisitionStatusBadge label={formatCell(value)} />;
      case "nextstep":
        return (
          <NextStepButton
            row={row}
            busy={busyId === rowKey(row)}
            onNextStep={onNextStep}
          />
        );
      case "resultfile":
        return (
          <ResultFileLink
            url={formatCell(value)}
            onOpen={(url) => onOpenFile(row, url)}
          />
        );
      case "printlabel":
        return (
          <PrintLabelCell
            printers={printers}
            onPrint={(p) => onPrintLabel(row, p)}
          />
        );
      case "note":
        return (
          <button
            type="button"
            className="btn btn-icon btn-sm h-28px w-28px btn-active-light-primary"
            title={t("Internal Notes")}
            aria-label={t("Internal Notes")}
            onClick={(e) => {
              e.stopPropagation();
              onOpenNotes(row);
            }}
          >
            <i className="bi bi-sticky fs-4 text-warning"></i>
          </button>
        );
      case "flag": {
        // Server-rendered flag markup; each flag carries data-flag-id (same as v1).
        const html = formatCell(value);
        if (!html) return null;
        return (
          <div
            className="vr2-flag-cell"
            onClick={(e) => {
              const el = (e.target as HTMLElement).closest("[data-flag-id]");
              const flagId = Number(el?.getAttribute("data-flag-id"));
              if (!flagId) return;
              e.stopPropagation();
              onOpenFlag(row, flagId);
            }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        );
      }
      default:
        return formatCell(value);
    }
  };

  let footer: React.ReactNode = null;
  if (!loading && !rows.length && !error)
    footer = canRemoveDateWindow ? (
      <button
        type="button"
        className="btn btn-sm btn-light"
        onClick={onRemoveDateWindow}
      >
        {t("Remove date window")}
      </button>
    ) : null;
  else if (loading) footer = t("Loading…");
  else if (rows.length)
    footer = capped ? (
      t(
        `Showing the first ${MAX_ROWS_IN_DOM.toLocaleString("en-US")} rows. Narrow your filters or use search.`
      )
    ) : hasMore ? (
      <button type="button" className="btn btn-sm btn-light" onClick={onLoadMore}>
        {t("Load more")}
      </button>
    ) : (
      t("End of results")
    );

  return (
    <section className="vr2-gridwrap table_bordered">
      {bulkBar}
      <div className="vr2-scroller" ref={scrollerRef}>
        <Table className="table table-cutome-expend table-bordered table-sticky-header table-head-bg table-bg table-head-custom table-vertical-center border-0 mb-0">
          <TableHead>
            <TableRow className="h-40px">
              <TableCell className="vr2-cb">
                <label className="form-check form-check-sm form-check-solid">
                  <input
                    id="ViewRequisitionV2CheckAll"
                    className="form-check-input"
                    type="checkbox"
                    aria-label={t("Select all loaded rows")}
                    checked={allSelected}
                    onChange={onToggleAll}
                  />
                </label>
              </TableCell>
              <TableCell className="vr2-act">{t("Actions")}</TableCell>
              {columns.map((c) => (
                <TableCell
                  key={c.id}
                  className={isSortable(c) ? "is-sortable" : ""}
                  aria-sort={
                    sort?.key === c.key
                      ? sort.dir === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                  onClick={() => isSortable(c) && onSort(c)}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <span>{t(c.label)}</span>
                    {renderArrows(c)}
                  </div>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => {
              const id = rowKey(row);
              const cls = [
                "vr2-row",
                selected.has(id) && "is-selected",
                (openId === id || focusIdx === i) && "is-focused",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <TableRow
                  key={id || `row-${i}`}
                  data-row-index={i}
                  className={cls}
                  onClick={() => onRowClick(row, i)}
                >
                  <TableCell
                    className="vr2-cb"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <label className="form-check form-check-sm form-check-solid">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        aria-label={t("Select row")}
                        checked={selected.has(id)}
                        onChange={() => onToggleRow(id)}
                      />
                    </label>
                  </TableCell>
                  <TableCell
                    className="vr2-act"
                    style={openMenuId === id ? { zIndex: 10 } : undefined}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <RowActionDropdown
                      items={rowMenuItems(row)}
                      show={openMenuId === id}
                      onToggle={(next) => setOpenMenuId(next ? id : null)}
                    />
                  </TableCell>
                  {columns.map((c) => (
                    <TableCell key={c.id}>{renderCell(row, c)}</TableCell>
                  ))}
                </TableRow>
              );
            })}
            {loading &&
              Array.from({ length: rows.length ? 3 : 10 }, (_, i) => (
                <TableRow key={`skel-${i}`}>
                  {Array.from({ length: colSpan }, (__, j) => (
                    <TableCell key={j}>
                      <Skeleton variant="text" width="80%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {!loading && !rows.length && !error && (
              <TableRow>
                <NoRecord colSpan={colSpan} message="No requisitions match." />
              </TableRow>
            )}
          </TableBody>
        </Table>
        <div ref={sentinelRef} className="vr2-foot">
          {footer}
        </div>
      </div>
    </section>
  );
};

export default React.memo(WorklistGrid);
